/**
 * Small presentational primitives shared by the admin console screens.
 * Inline-styled on purpose — no new global CSS classes (house rule for parallel agents).
 */
import type { CSSProperties, ReactNode } from "react";
import { ApiError } from "../../lib/api.js";

/** Visual on/off switch (green when on) — used by regions, flags, payments, MFA policy. */
export function Switch({
  on,
  disabled,
  onToggle,
}: {
  on: boolean;
  disabled?: boolean;
  onToggle: () => void;
}): ReactNode {
  const track: CSSProperties = {
    width: 36,
    height: 20,
    borderRadius: 999,
    border: "1px solid var(--line)",
    background: on ? "var(--good)" : "var(--line-input)",
    position: "relative",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    padding: 0,
    flex: "none",
    transition: "background .15s",
  };
  const knob: CSSProperties = {
    position: "absolute",
    top: 2,
    left: on ? 18 : 2,
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "var(--rail-on)",
    boxShadow: "var(--shadow-sm)",
    transition: "left .15s",
  };
  return (
    <button type="button" aria-pressed={on} disabled={disabled} onClick={onToggle} style={track}>
      <span style={knob} />
    </button>
  );
}

/**
 * Centered modal over a blurred backdrop; backdrop click closes.
 *
 * Structured like .rq-modal-h/-b/-f: pinned title, SCROLLING body, optional pinned footer.
 * The card is capped at the viewport (maxHeight) and the overlay top-aligns with the card on
 * margin:auto — centre-aligned flex overflows in BOTH directions on a tall dialog, which made
 * the New-org form unreachable at 1080p at both edges. Content taller than the cap now scrolls
 * inside the body while the title (and any footer) stay visible.
 */
export function Modal({
  title,
  onClose,
  children,
  footer,
  maxWidth = 480,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Pinned action row below the scrolling body — call sites without it keep actions inline. */
  footer?: ReactNode;
  maxWidth?: number | string;
}): ReactNode {
  // `overflowY: auto` on the OVERLAY was fighting the card's own maxHeight: a card taller than
  // the viewport made the overlay scrollable, and because the overlay scrolls the whole dialog
  // (title and footer included) the body never had to shrink — so the pinned footer scrolled off
  // the bottom and the dialog looked cut off with nothing to scroll inside it. The overlay is now
  // a fixed, non-scrolling frame and the BODY is the only thing that scrolls.
  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "color-mix(in srgb, var(--frame) 55%, transparent)",
    backdropFilter: "blur(2px)",
    zIndex: 60,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    overflow: "hidden",
  };
  return (
    <div
      style={overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="card pad"
        style={{
          maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth,
          width: "100%",
          boxShadow: "var(--shadow)",
          // dvh over vh: on mobile browsers vh counts the area behind the retracting URL bar,
          // so a vh-sized dialog is taller than what you can actually see.
          maxHeight: "calc(100dvh - 32px)",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <div style={{ fontWeight: 650, marginBottom: 10, flex: "none" }}>{title}</div>
        {/* flex:1 + minHeight:0 is the pair that makes this scroll. minHeight:0 alone only lets
            the body shrink if something else forces it to; flex:1 makes it take the leftover
            height and no more, so anything past that scrolls HERE rather than pushing the footer
            out of the dialog. The negative margin + matching padding keeps a focus ring from
            being clipped by the scroll container. */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            margin: "0 -4px",
            padding: "0 4px",
          }}
        >
          {children}
        </div>
        {footer && <div style={{ flex: "none", marginTop: 12 }}>{footer}</div>}
      </div>
    </div>
  );
}

/** Simple vertical bar chart (flex columns) for trends — lime by default. */
export function TrendBars({
  bars,
  color = "var(--lime-strong)",
  height = 120,
}: {
  bars: { label: string; value: number }[];
  color?: string;
  height?: number;
}): ReactNode {
  const max = Math.max(1, ...bars.map((b) => b.value));
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height }}>
      {bars.map((b, i) => (
        <div
          key={`${b.label}-${i}`}
          title={b.label}
          style={{
            flex: 1,
            height: `${Math.max((b.value / max) * 100, 2)}%`,
            background: color,
            borderRadius: 3,
            minWidth: 3,
          }}
        />
      ))}
    </div>
  );
}

/** Map an ApiError into a friendly, admin-console-appropriate message. */
export function apiErrorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return "You don't have permission for that — this action is owner-only.";
    if (err.status === 400) return "Invalid input — check the fields and try again.";
    if (err.status === 404) return "Not found — it may have been removed.";
  }
  return fallback;
}
